import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, Button, ButtonProps } from '@chakra-ui/core';
import { ChevronDownIcon } from '@chakra-ui/icons';

export interface DropdownMenuItem {
  id: string;
  label: string;
  description?: string;
  isSelected?: boolean;
  isDisabled?: boolean;
}

export interface CustomDropdownMenuProps {
  label: string;
  items: DropdownMenuItem[];
  maxHeight?: string;
  size?: 'sm' | 'md' | 'lg';
  buttonProps?: ButtonProps;
  isInvalid?: boolean;
  renderItem: (item: DropdownMenuItem, index: number) => React.ReactNode;
}

export const CustomDropdownMenu: React.FC<CustomDropdownMenuProps> = ({
  label,
  items,
  maxHeight = '300px',
  size = 'sm',
  buttonProps = {},
  isInvalid = false,
  renderItem,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleToggle = useCallback(() => {
    if (isOpen) {
      handleClose();
    } else {
      handleOpen();
    }
  }, [isOpen, handleOpen, handleClose]);

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleClose]);

  return (
    <Box position="relative" display="inline-block">
      <Button
        ref={buttonRef}
        size={size}
        variant="outline"
        rightIcon={<ChevronDownIcon />}
        onClick={handleToggle}
        px={3}
        fontWeight="600"
        borderColor={isInvalid ? 'red.500' : 'blue.200'}
        color={isInvalid ? 'red.500' : 'blue.600'}
        bg={isInvalid ? 'red.50' : 'white'}
        _hover={{ bg: 'blue.50' }}
        _active={{ bg: 'blue.100' }}
        aria-invalid={isInvalid}
        {...buttonProps}
      >
        {label}
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <Box
          ref={menuRef}
          position="absolute"
          minW={`${buttonRef.current?.offsetWidth}px`}
          bottom={0}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
          maxH={maxHeight}
          overflowY="auto"
          zIndex={1500}
          py={2}
          id="dropdown-menu"
        >
          {items.map((item, index) => (
            <Box key={item.id}>{renderItem(item, index)}</Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default CustomDropdownMenu;
